import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { AlertTriangle, CheckCircle, Loader, Package, Printer, Truck } from 'lucide-react';
import { velocityApi } from '../../services/api';
import {
  VELOCITY_DEFAULT_BOX,
  VELOCITY_LABEL_TTL_NOTE,
  buildVelocityBox,
  isVelocityBookedButUnsaved,
  velocityErrorMessage,
  velocityTotalCharges,
} from '../../utils/velocity';

const BOX_FIELDS = [
  { key: 'weightKg',  label: 'Weight (kg)', step: '0.1' },
  { key: 'lengthCm',  label: 'Length (cm)', step: '1' },
  { key: 'breadthCm', label: 'Breadth (cm)', step: '1' },
  { key: 'heightCm',  label: 'Height (cm)', step: '1' },
];

const money = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? `₹${n.toFixed(2)}` : '—';
};

/**
 * "Ship with Velocity" dialog.
 * Khulte hi serviceability + preview parallel chalte hain. Rates optional hai
 * (user maange to). Ship ek hi call hai — backend payload khud banata hai.
 */
const VelocityShipModal = ({ open, order, onClose, onShipped }) => {
  const orderId = order?.id;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [serviceability, setServiceability] = useState(null);
  const [preview, setPreview] = useState(null);

  const [box, setBox] = useState(VELOCITY_DEFAULT_BOX);
  const [carrierId, setCarrierId] = useState('');
  const [remarks, setRemarks] = useState('');

  const [rates, setRates] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesError, setRatesError] = useState(null);

  const [shipping, setShipping] = useState(false);
  const [error, setError] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [result, setResult] = useState(null);

  const previewTimer = useRef(null);

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    setLoadError(null);
    const [svc, pre] = await Promise.allSettled([
      velocityApi.getServiceability(orderId, 'forward'),
      velocityApi.preview(orderId, {}, 'forward'),
    ]);
    if (svc.status === 'fulfilled') setServiceability(svc.value);
    else setLoadError(velocityErrorMessage(svc.reason));
    if (pre.status === 'fulfilled') setPreview(pre.value);
    setLoading(false);
  }, [orderId]);

  // Modal khulte hi sab reset + load
  useEffect(() => {
    if (!open) return;
    setBox(VELOCITY_DEFAULT_BOX);
    setCarrierId('');
    setRemarks('');
    setRates(null);
    setRatesError(null);
    setError(null);
    setBlocked(false);
    setResult(null);
    load();
  }, [open, load]);

  // Box badla to preview refresh (preview Velocity ko call nahi karta, sasta hai)
  useEffect(() => {
    if (!open || !orderId || result) return;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => {
      velocityApi
        .preview(orderId, buildVelocityBox(box), 'forward')
        .then(setPreview)
        .catch(() => {});
    }, 500);
    return () => previewTimer.current && clearTimeout(previewTimer.current);
  }, [box, open, orderId, result]);

  const loadRates = async () => {
    setRatesLoading(true);
    setRatesError(null);
    try {
      setRates(await velocityApi.getRates(orderId, buildVelocityBox(box), 'forward'));
    } catch (err) {
      setRatesError(velocityErrorMessage(err));
    } finally {
      setRatesLoading(false);
    }
  };

  const handleShip = async () => {
    if (shipping || blocked) return;
    setShipping(true);
    setError(null);
    try {
      const body = { ...buildVelocityBox(box) };
      if (carrierId) body.carrierId = carrierId;
      if (remarks.trim()) body.remarks = remarks.trim();
      const shipment = await velocityApi.ship(orderId, body);
      setResult(shipment);
      onShipped?.(shipment);
    } catch (err) {
      setError(velocityErrorMessage(err, { action: 'ship' }));
      // 409 = Velocity par book ho chuka, CRM update fail — dobara ship mat karao
      if (isVelocityBookedButUnsaved(err)) setBlocked(true);
    } finally {
      setShipping(false);
    }
  };

  const carriers = serviceability?.carriers || [];
  const notServiceable = !!serviceability && serviceability.serviceable === false;
  const serviceMessage =
    serviceability?.message ||
    serviceability?.error ||
    'This pincode cannot be shipped with Velocity.';
  const payload = preview?.payload;
  const rateFor = (id) => (rates?.serviceable_couriers || []).find((c) => c.carrier_id === id);
  const canShip = !loading && !loadError && !notServiceable && !shipping && !blocked;

  const inputCls =
    'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200';

  return (
    <Modal
      title={result ? '✅ Shipped with Velocity' : '🚚 Ship with Velocity'}
      open={open}
      onCancel={() => !shipping && onClose?.()}
      footer={null}
      width={640}
      closable={!shipping}
      maskClosable={!shipping}
      destroyOnClose
    >
      {result ? (
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-green-50 border border-green-200 flex items-start gap-3">
            <CheckCircle className="w-6 h-6 text-green-600 mt-0.5" />
            <div className="flex-1">
              <p className="font-bold text-green-800">Booked with {result.courierName || 'Velocity'}</p>
              <p className="mt-1 text-sm">
                AWB <span className="font-mono font-bold">{result.awbCode || 'pending'}</span>
              </p>
              {velocityTotalCharges(result.charges) !== null && (
                <p className="mt-1 text-sm text-green-800">
                  Charges {money(velocityTotalCharges(result.charges))}
                </p>
              )}
            </div>
          </div>

          {result.labelUrl ? (
            <div className="p-3 rounded-xl border border-amber-200 bg-amber-50">
              <a
                href={result.labelUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold"
              >
                <Printer className="w-4 h-4" /> Print label
              </a>
              <p className="mt-2 text-xs text-amber-800">{VELOCITY_LABEL_TTL_NOTE}</p>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No label link returned yet.</p>
          )}

          <div className="flex justify-end">
            <button type="button" className="px-6 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {loading && (
            <div className="py-10 flex flex-col items-center gap-3 text-gray-500">
              <Loader className="w-6 h-6 animate-spin" />
              <span className="text-sm">Checking serviceability…</span>
            </div>
          )}

          {!loading && loadError && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
              {loadError}
              <button type="button" className="ml-3 underline font-semibold" onClick={load}>
                Retry
              </button>
            </div>
          )}

          {!loading && !loadError && notServiceable && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 flex items-start gap-2 text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              <span>{serviceMessage}</span>
            </div>
          )}

          {!loading && payload && (
            <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 text-sm space-y-1">
              <p className="text-[11px] font-black uppercase tracking-widest text-gray-400">Will be sent to Velocity</p>
              <p className="font-semibold text-gray-800">
                {[payload.billing_customer_name, payload.billing_last_name].filter(Boolean).join(' ')} · {payload.billing_phone}
              </p>
              <p className="text-gray-600">{payload.billing_address}</p>
              <p className="text-gray-600">
                {payload.billing_city}, {payload.billing_state} — <span className="font-mono">{payload.billing_pincode}</span>
                {serviceability?.zone ? <span className="ml-2 text-gray-400">({serviceability.zone})</span> : null}
              </p>
              <p className="pt-1">
                <span className="font-semibold">{payload.payment_method}</span>
                {' · collect '}
                <span className="font-semibold">{money(payload.cod_collectible)}</span>
                {' · order total '}
                {money(payload.sub_total)}
              </p>
              {Array.isArray(payload.order_items) && payload.order_items.length > 0 && (
                <ul className="pt-2 space-y-1">
                  {payload.order_items.map((it, i) => (
                    <li key={`${it.sku}-${i}`} className="flex items-center gap-2 text-gray-600">
                      <Package className="w-3.5 h-3.5 text-gray-400" />
                      <span className="flex-1 truncate">{it.name}</span>
                      <span className="text-gray-400">×{it.units}</span>
                      <span className="font-mono">{money(it.selling_price)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {payload.pickup_location && (
                <p className="pt-1 text-xs text-gray-500">Pickup: {payload.pickup_location}</p>
              )}
            </div>
          )}

          {!loading && !notServiceable && (
            <>
              <div className="grid grid-cols-4 gap-2">
                {BOX_FIELDS.map((f) => (
                  <label key={f.key} className="text-xs font-semibold text-gray-600">
                    {f.label}
                    <input
                      type="number"
                      min="0"
                      step={f.step}
                      className={`${inputCls} mt-1`}
                      value={box[f.key]}
                      onChange={(e) => setBox((p) => ({ ...p, [f.key]: e.target.value }))}
                      disabled={shipping}
                    />
                  </label>
                ))}
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-600" htmlFor="velocity-carrier">
                    Carrier
                  </label>
                  <button
                    type="button"
                    className="text-xs font-semibold underline text-gray-500 disabled:opacity-50"
                    onClick={loadRates}
                    disabled={ratesLoading || shipping}
                  >
                    {ratesLoading ? 'Loading price…' : 'Show price & ETA'}
                  </button>
                </div>
                <select
                  id="velocity-carrier"
                  className={`${inputCls} mt-1`}
                  value={carrierId}
                  onChange={(e) => setCarrierId(e.target.value)}
                  disabled={shipping}
                >
                  <option value="">Auto (Velocity chooses)</option>
                  {carriers.map((c) => {
                    const r = rateFor(c.carrierId);
                    const extra = r
                      ? ` — ${money(r.charges?.total_forward_charges)}${r.expected_delivery?.delivery ? `, ETA ${r.expected_delivery.delivery}` : ''}`
                      : '';
                    return (
                      <option key={c.carrierId} value={c.carrierId}>
                        {c.carrierName}
                        {extra}
                      </option>
                    );
                  })}
                </select>
                {ratesError && <p className="mt-1 text-xs text-amber-700">{ratesError}</p>}
              </div>

              <label className="block text-xs font-semibold text-gray-600">
                Remarks (optional)
                <input
                  type="text"
                  className={`${inputCls} mt-1`}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="e.g. Fragile"
                  disabled={shipping}
                  maxLength={200}
                />
              </label>
            </>
          )}

          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
              {error}
              {blocked && (
                <p className="mt-1 font-semibold">Do not ship this order again — check the Velocity panel.</p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              className="px-5 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600 disabled:opacity-50"
              onClick={onClose}
              disabled={shipping}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-6 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
              onClick={handleShip}
              disabled={!canShip}
            >
              {shipping ? <Loader className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
              {shipping ? 'Booking…' : 'Ship'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default VelocityShipModal;
